import type { ApplicationDetail, UserProfile } from 'types/api'

/**
 * Required profile fields for completion calculation
 * ID number OR passport number is required based on country
 */
const PROFILE_REQUIRED_FIELDS = [
  'first_name',
  'last_name',
  'birth_date',
  'nationality',
  'country',
  'phone',
  'street',
  'city',
  'zip'
] as const

/**
 * Required application fields for completion calculation
 */
const APPLICATION_REQUIRED_FIELDS = [
  'locations',
  'arrival_date',
  'rent_min',
  'rent_max'
] as const

/**
 * Check if a value is considered "filled"
 */
function isFilled(value: unknown): boolean {
  if (value === null || value === undefined) return false
  if (typeof value === 'string' && value.trim() === '') return false
  if (Array.isArray(value) && value.length === 0) return false
  return true
}

/**
 * Calculate profile completion percentage
 * @param profile User profile data
 * @returns Completion percentage (0-100)
 */
export function calculateProfileCompletion(
  profile: UserProfile | undefined
): number {
  if (!profile) return 0

  let filledCount = 0
  let totalRequired = PROFILE_REQUIRED_FIELDS.length + 1 // +1 for id_number OR passport_number

  // Check standard required fields
  for (const field of PROFILE_REQUIRED_FIELDS) {
    if (isFilled(profile[field])) {
      filledCount++
    }
  }

  // Check id_number OR passport_number (at least one must be filled)
  if (isFilled(profile.id_number) || isFilled(profile.passport_number)) {
    filledCount++
  }

  return Math.round((filledCount / totalRequired) * 100)
}

/**
 * Calculate application completion percentage
 * @param application Application data
 * @returns Completion percentage (0-100)
 */
export function calculateApplicationCompletion(
  application: ApplicationDetail | undefined
): number {
  if (!application) return 0

  let filledCount = 0
  const totalRequired = APPLICATION_REQUIRED_FIELDS.length

  for (const field of APPLICATION_REQUIRED_FIELDS) {
    const value = application[field]
    if (isFilled(value)) {
      filledCount++
    }
  }

  return Math.round((filledCount / totalRequired) * 100)
}

/**
 * Calculate overall onboarding completion
 * Profile: 50%, Application: 50%
 * @param profile User profile data
 * @param application Application data
 * @returns Overall completion percentage (0-100)
 */
export function calculateOverallCompletion(
  profile: UserProfile | undefined,
  application: ApplicationDetail | undefined
): number {
  const profileCompletion = calculateProfileCompletion(profile)
  const applicationCompletion = calculateApplicationCompletion(application)

  return Math.round((profileCompletion + applicationCompletion) / 2)
}
